import { useCallback, useEffect, useRef } from 'react'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { serializeComplianceDocumentSectionsForDirtyCheck } from './serializeComplianceDocumentSectionsForDirtyCheck'

const AUTOSAVE_DELAY_MS = 2500
const BLUR_FLUSH_DELAY_MS = 150

type UseComplianceDocumentSectionsPersistenceOptions<T> = {
  baselineSections: T[]
  baselineKey: number
  onSectionsChange: (sections: T[]) => void
  onAutosave: (sections: T[]) => Promise<void>
  flushAllSectionsToState: () => T[]
  reindexSections: (sections: T[]) => T[]
  isGenerating: boolean
}

export function useComplianceDocumentSectionsPersistence<
  T extends {
    sectionId: string
    parentSectionId?: string | null
    title: string
    kind: string
    sortOrder: number
    markdown: string
  },
>({
  baselineSections,
  baselineKey,
  onSectionsChange,
  onAutosave,
  flushAllSectionsToState,
  reindexSections,
  isGenerating,
}: UseComplianceDocumentSectionsPersistenceOptions<T>) {
  const lastPersistedSnapshotRef = useRef<string>(
    serializeComplianceDocumentSectionsForDirtyCheck(baselineSections)
  )
  const baselineSectionsRef = useRef(baselineSections)

  useEffect(() => {
    baselineSectionsRef.current = baselineSections
  }, [baselineSections])

  const flushSections = useCallback(() => {
    return reindexSections(flushAllSectionsToState())
  }, [flushAllSectionsToState, reindexSections])

  const persistDraft = useCallback(
    async (options?: { force?: boolean }) => {
      const flushedSections = flushSections()
      const snapshot =
        serializeComplianceDocumentSectionsForDirtyCheck(flushedSections)

      if (!options?.force && snapshot === lastPersistedSnapshotRef.current) {
        return
      }

      lastPersistedSnapshotRef.current = snapshot
      onSectionsChange(flushedSections)
      await onAutosave(flushedSections)
    },
    [flushSections, onAutosave, onSectionsChange]
  )

  const persistDraftRef = useRef(persistDraft)
  const isGeneratingRef = useRef(isGenerating)

  useEffect(() => {
    persistDraftRef.current = persistDraft
  }, [persistDraft])

  useEffect(() => {
    isGeneratingRef.current = isGenerating
  }, [isGenerating])

  const { schedule, flushPending, cancel } = useDebouncedCallback(
    () => persistDraftRef.current(),
    AUTOSAVE_DELAY_MS
  )

  const { schedule: scheduleBlurFlush } = useDebouncedCallback(
    () => {
      if (isGeneratingRef.current) {
        return
      }
      void flushPending()
    },
    BLUR_FLUSH_DELAY_MS
  )

  useEffect(() => {
    lastPersistedSnapshotRef.current =
      serializeComplianceDocumentSectionsForDirtyCheck(
        reindexSections(baselineSectionsRef.current)
      )
    cancel()
  }, [baselineKey, cancel, reindexSections])

  const scheduleAutosave = useCallback(() => {
    if (isGenerating) {
      return
    }
    schedule()
  }, [isGenerating, schedule])

  const scheduleBlurAutosave = useCallback(() => {
    if (isGenerating) {
      return
    }
    scheduleBlurFlush()
  }, [isGenerating, scheduleBlurFlush])

  const flushAndAutosaveSilent = useCallback(async () => {
    cancel()
    await persistDraft({ force: true })
  }, [cancel, persistDraft])

  const persistSectionsImmediately = useCallback(
    async (sectionsToPersist: T[]) => {
      cancel()
      const normalizedSections = reindexSections(sectionsToPersist)
      lastPersistedSnapshotRef.current =
        serializeComplianceDocumentSectionsForDirtyCheck(normalizedSections)
      onSectionsChange(normalizedSections)
      await onAutosave(normalizedSections)
    },
    [cancel, onAutosave, onSectionsChange, reindexSections]
  )

  return {
    flushSections,
    scheduleAutosave,
    scheduleBlurAutosave,
    flushAndAutosaveSilent,
    persistSectionsImmediately,
    cancelAutosave: cancel,
  }
}
